import { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { listUsers } from 'services/calendarServices';
import { getActivityLogs } from 'services/activitylogServices';

import { addDays, subtractDays } from 'utils/timeDateUtils';
import { generateFullName } from 'utils/generateFullNameUtils';

import ActivityLogTable from './ActivityLogTable';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { paginationDropdownOptions } from 'utils/utils';
import { getDateBoundaryInTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { Tooltip } from 'primereact/tooltip';
import { activityLogModuleFilter, caseLeadsFilter } from 'constants/dropdownOptions';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { handlePageNumberInPagination } from 'common/pagination';
import CaseSelect from 'components/UI/CaseSelect/CaseSelect';

const ActivityLog = (props) => {
  const userContext = useUserDetailsContext();

  const [activityLog, setActivityLog] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedUserList, setSelectedUserList] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [dateFilter, setDateFilter] = useState('');
  const [filterBy, setFilterBy] = useState();
  const [module, setModule] = useState();
  const [moduleSection, setModuleSection] = useState();
  const [customDateRange, setCustomDateRange] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [pageLimit, setPageLimit] = useState(() => {
    const pref = userContext?.firmDetails?.pagination_preference;
    if (props?.isFromTeamManagement) return pref?.settings?.team_management?.self ?? 10;
    if (props?.isFromReports) return pref?.report?.activity_log ?? 10;
    if (props?.isCaseSpecific && props?.isLead) return pref?.leads?.activity_log ?? 10;
    if (props?.isCaseSpecific) return pref?.case?.activity_log ?? 10;
    if (props?.isFromDashBoard) return pref?.dashboard?.activity_log ?? 10;
    return pref?.report?.activity_log ?? 10;
  });
  const [activityLogResponse, setActivityLogResponse] = useState(null);
  const [isActivityLogForward, setIsActivityLogForward] = useState(true);
  const [activityLogFetchBody, setActivityLogFetchBody] = useState(null);

  const activity_next_Token = activityLogResponse?.data?.next_token ?? null;
  const activity_transition_Token = activityLogResponse?.data?.transition_token ?? null;

  const [currentDateToDisplay, setCurrentDateToDisplay] = useState({
    start: new Date(),
    end: new Date(),
  });

  const isCaseSpecific = props?.isCaseSpecific ?? false;
  const isFromTeamManagement = props?.isFromTeamManagement ?? false;
  const isFromDashBoard = props?.isFromDashBoard ?? false;
  const isFromReports = props?.isFromReports ?? false;
  const userId = props?.userDetails?.client_cognito_username;

  let todayDate = new Date();

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line

  const handleActivityLogPageLimitChange = (event) => {
    setPageLimit(event.value);
    if (isFromTeamManagement) {
      handlePageNumberInPagination(userContext, 'settings', 'team_management', 'self', event.value);
    } else if (isFromReports) {
      handlePageNumberInPagination(userContext, 'report', '', 'activity_log', event.value);
    } else if (isCaseSpecific && props?.isLead) {
      handlePageNumberInPagination(userContext, 'leads', '', 'activity_log', event.value);
    } else if (isCaseSpecific) {
      handlePageNumberInPagination(userContext, 'case', '', 'activity_log', event.value);
    } else if (isFromDashBoard) {
      handlePageNumberInPagination(userContext, 'dashboard', '', 'activity_log', event.value);
    } else {
      handlePageNumberInPagination(userContext, 'report', '', 'activity_log', event.value);
    }
    setIsActivityLogForward(true);
  };

  const loadUsers = (case_id = props?.caseDetails?.case_id) => {
    listUsers(case_id, false, false, false, false, true)
      .then((response) => {
        const sorted = [...(response.data ?? [])].sort((a, b) => {
          const aHasFirm = !!a.firm_user_id;
          const bHasFirm = !!b.firm_user_id;
          if (aHasFirm !== bHasFirm) return aHasFirm ? -1 : 1;
          const aName = (a.first_name ?? '').toLowerCase();
          const bName = (b.first_name ?? '').toLowerCase();
          return aName.localeCompare(bName);
        });
        setUsersList(sorted.map((v) => ({ ...v, label: generateFullName(v) })));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getDataOfActivityLog('initial', customDateRange);
  }, [selectedUserList, filterBy, moduleSection, dateFilter, customDateRange, pageLimit, refresh]);

  const fetchActivityLogData = (backward = false, forward = false) => {
    if (backward) {
      getDataOfActivityLog('backward');
    } else if (forward) {
      let currentDate = new Date();

      if (endDate.toDateString() !== currentDate.toDateString()) {
        getDataOfActivityLog('forward');
      }
    }
  };

  function getDataOfActivityLog(type, date) {
    let tempStartDate = new Date();
    let tempEndDate = new Date();

    if (type === 'forward') {
      tempStartDate = addDays(endDate, 1);
      tempEndDate = addDays(endDate, 2);
    } else if (type === 'backward') {
      tempStartDate = subtractDays(endDate, 3);
      tempEndDate = subtractDays(endDate, 2);
    } else if (type === 'initial') {
      tempStartDate = subtractDays(endDate, 1);
    }

    setEndDate(tempEndDate);
    setCurrentDateToDisplay({ start: tempStartDate, end: tempEndDate });
    setActivityLog([]);

    let data = {
      key: '',
      status: '',
      type: filterBy ? filterBy : '',
      sub_type: '',
      user_id: isFromTeamManagement ? userId : selectedUserList?.client_cognito_username ? selectedUserList?.client_cognito_username : '',
      case_id: isCaseSpecific ? props?.caseDetails?.case_id : moduleSection?.value ?? '',
      start_timestamp: date?.start_date
        ? date?.start_date
        : getDateBoundaryInTimezone(date?.start_date, userContext?.userDetails?.timezone, 'start'),
      end_timestamp: date?.end_date
        ? date?.end_date
        : getDateBoundaryInTimezone(date?.start_date, userContext?.userDetails?.timezone, 'end'),
      pagination_limit: pageLimit,
      pagination_token: '',
      pagination_direction: 'forward',
    };

    setActivityLogFetchBody({
      key: data.key,
      status: data.status,
      type: data.type,
      sub_type: data.sub_type,
      user_id: data.user_id,
      case_id: data.case_id,
      start_timestamp: data.start_timestamp,
      end_timestamp: data.end_timestamp,
    });
    setIsActivityLogForward(true);
    getActivityLogData(data);
  }

  const onActivityLogNextPageClick = () => {
    if (!activityLogFetchBody) return;
    getActivityLogData({
      ...activityLogFetchBody,
      pagination_limit: pageLimit,
      pagination_token: !isActivityLogForward ? activity_transition_Token : activity_next_Token,
      pagination_direction: 'forward',
    });
    setIsActivityLogForward(true);
  };

  const onActivityLogPrevPageClick = () => {
    if (!activityLogFetchBody) return;
    getActivityLogData({
      ...activityLogFetchBody,
      pagination_limit: pageLimit,
      pagination_token: isActivityLogForward ? activity_transition_Token : activity_next_Token,
      pagination_direction: 'reverse',
    });
    setIsActivityLogForward(false);
  };

  function getActivityLogData(data) {
    getActivityLogs(data)
      .then((response) => {
        setActivityLog([...(response?.data?.activity_logs ?? [])]);
        setActivityLogResponse(response);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const activityLogPaginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <div className="d-flex align-items-center gap-2 flex-shrink-0 ps-1">
          <span className="text-nowrap" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Items per page
          </span>
          <Dropdown
            className="flex-shrink-0"
            style={{ minHeight: '2.25rem' }}
            value={options.value}
            options={paginationDropdownOptions}
            onChange={handleActivityLogPageLimitChange}
          />
        </div>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button
          type="button"
          onClick={onActivityLogNextPageClick}
          disabled={activity_next_Token === null && isActivityLogForward}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button
          type="button"
          onClick={onActivityLogPrevPageClick}
          disabled={
            activity_transition_Token === null || (activity_transition_Token && activity_next_Token === null && !isActivityLogForward)
          }
          className={'p-paginator-prev p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
  };

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };

  const pickExtractedDate = (dateArray) => {
    return {
      start_date: dateArray?.[0] || '',
      end_date: dateArray?.[1] || dateArray?.[0] || '',
    };
  };

  const onChangeCalendar = (value) => {
    setDateFilter(value);
    if (value) {
      setCustomDateRange(pickExtractedDate(value));
    } else {
      setCustomDateRange(null);
    }
  };

  const refreshCase = () => {
    setDateFilter('');
    setCustomDateRange(null);
    setSelectedUserList(null);
    setModule(null);
    setModuleSection(null);
    setFilterBy();
    setRefresh(!refresh);
  };

  const onChangeCaseOrLeadsStatus = (e) => {
    setFilterBy(e?.value);
  };

  return (
    <div>
      {!isFromDashBoard && <GlobalLoader />}
      <div className="card-bg p-3 mt-3">
        {!isFromTeamManagement && !isFromReports && <h5 className="F-size14 mb-3 black-600 text-bold">Activity Log</h5>}
        {isFromReports && <h5 className="F-size14 mb-3 black-600 text-bold">Activity Log</h5>}
        <div className="d-flex justify-content-end align-items-center flex-wrap mb-3 filter-wrap">
          {isFromTeamManagement ? (
            <div className="d-flex align-items-center">
              <div className="me-3 black-800 text-bold">
                {currentDateToDisplay.end.toDateString().split(' ')[1]} {currentDateToDisplay.start.toDateString().split(' ')[2]}-
                {currentDateToDisplay.end.toDateString().split(' ')[2]}
              </div>

              <div
                className="rounded me-1 pointer slider-ctrl"
                onClick={(e) => {
                  fetchActivityLogData(true, false);
                }}
              >
                <i className="pi pi-angle-left p-1"></i>
              </div>
              <div
                className={
                  currentDateToDisplay.end.toDateString() === todayDate.toDateString()
                    ? 'rounded ms-1 pointer slider-ctrl disabled-element '
                    : 'rounded ms-1 pointer slider-ctrl'
                }
                onClick={(e) => fetchActivityLogData(false, true)}
              >
                <i className="pi pi-angle-right p-1"></i>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-wrap align-items-center p-2 filter-wraper gap-2">
              <label className="text-bold black-600  filter-label">Filters:</label>
              <div className="d-flex flex-column filter-wraper">
                <TimezoneDatePicker
                  timezone={userContext?.userDetails?.timezone}
                  className="input-shadow filter-calender"
                  id="icon"
                  value={dateFilter}
                  onChange={(e) => {
                    onChangeCalendar(e);
                  }}
                  selectionMode="range"
                  showIcon
                  placeholder="Date"
                  isEndOfDay={true}
                />
              </div>
              <div className="d-flex flex-column filter-wraper">
                <Dropdown
                  placeholder={props?.caseDetails?.case_id ? 'By Case User' : 'By User'}
                  onChange={(e) => setSelectedUserList(e.value)}
                  optionLabel="label"
                  options={usersList}
                  value={selectedUserList}
                  className="p-datatable-responsive"
                  filter
                  showClear
                  style={{ minWidth: 200 }}
                  itemTemplate={assignToItemTemplate}
                  filterBy="label,first_name,last_name,middle_name,title"
                  filterMatchMode="contains"
                />
              </div>
              <div className="d-flex flex-column filter-wraper">
                <Dropdown options={activityLogModuleFilter} onChange={onChangeCaseOrLeadsStatus} value={filterBy} placeholder="Module" />
              </div>
              {isFromReports && (
                <div className="d-flex flex-column filter-wraper">
                  <Dropdown options={caseLeadsFilter} onChange={(e) => setModule(e?.value)} value={module} placeholder="Sub Module" />
                </div>
              )}

              {isFromReports && (module === 'case' || module === 'lead') && (
                <div className="d-flex flex-column filter-wraper">
                  <CaseSelect
                    value={moduleSection}
                    onChange={(option) => setModuleSection(option)}
                    placeholder={module === 'case' ? 'Select Case' : 'Select Lead'}
                    filterBy={module === 'case' ? 'case' : 'lead'}
                  />
                </div>
              )}

              <div className="d-flex flex-column mx-2">
                <div className="pointer ms-0">
                  <i className="fas fa-sync-alt" onClick={refreshCase}></i>
                  <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
                </div>
              </div>
            </div>
          )}
        </div>
        <ActivityLogTable
          activityLog={activityLog}
          isCaseSpecific={isCaseSpecific}
          isLead={props?.isLead}
          isFromDashBoard={isFromDashBoard}
          isFromTeamManagement={isFromTeamManagement}
          isFromReports={isFromReports}
          pageLimit={pageLimit}
          onPageLimitChange={handleActivityLogPageLimitChange}
          paginatorTemplate={activityLogPaginatorTemplate}
        />
      </div>
    </div>
  );
};

export default ActivityLog;
